
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, Row, Spinner } from "react-bootstrap";
// import { addDoc, collection, getDocs, serverTimestamp } from "firebase/firestore";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { db } from "../../firebase";
// import { cleanObject } from "../../utils/eventUtils";
import {
  calculateAutoAmount,
  cleanObject,
} from "../../utils/eventUtils";
import * as XLSX from "xlsx";

const PAYMENT_METHODS = [
  "Cash",
  "PhonePe",
  "Google Pay",
  "UPI",
  "Bank Transfer",
];

const FUND_TYPES = ["Donation", "Sponsor", "Gift", "Sale", "Other"];

const currentYearMonthPrefix = () => {
  const date = new Date();
  const year = String(date.getFullYear()).slice(-2);
  const month = String(date.getMonth() + 1).padStart(2, "0");

  return `${year}${month}`;
};

const parseReceiptNumber = (receiptNumber) => {
  const normalizedReceiptNumber = String(receiptNumber || "").trim();
  const match = normalizedReceiptNumber.match(/^(\d{4})(\d{4,})$/);

  if (!match) {
    return null;
  }

  const sequence = Number(match[2]);

  if (!Number.isSafeInteger(sequence) || sequence < 1) {
    return null;
  }

  return {
    prefix: match[1],
    sequence,
  };
};

const buildReceiptNumber = (prefix, sequence) => {
  return `${prefix}${String(sequence).padStart(4, "0")}`;
};

const extractFloor = (label) => {
  const text = String(label || "").trim().toUpperCase();

  if (text.includes("-G") || text.startsWith("G")) {
    return 0;
  }

  const numbers = text.match(/\d+/g);

  if (!numbers?.length) {
    return null;
  }

  const firstNumber = Number(numbers[0]);

  if (!Number.isFinite(firstNumber)) {
    return null;
  }

  return firstNumber >= 100
    ? Math.floor(firstNumber / 100)
    : firstNumber;
};

const normalizeFundType = (value) => {
  const normalizedValue = String(
    value || ""
  )
    .trim()
    .toLowerCase();

  const fundTypes = {
    donation: "Donation",
    sponsor: "Sponsor",
    gift: "Gift",
    sale: "Sale",
    other: "Other",
  };

  return (
    fundTypes[normalizedValue] ||
    "Other"
  );
};

const sanitizeFileName = (value) => {
  return String(value || "Event")
    .trim()
    .replace(/[\\/:*?"<>|]/g, "_")
    .replace(/\s+/g, "_");
};

const getReceiptSeries = async (
  eventId
) => {
  const snapshot = await getDocs(
    collection(
      db,
      "events",
      eventId,
      "funds"
    )
  );

  const validReceipts =
    snapshot.docs
      .map((fundDocument) =>
        parseReceiptNumber(
          fundDocument.data()
            ?.receiptNo
        )
      )
      .filter(Boolean);

  if (validReceipts.length === 0) {
    return {
      prefix:
        currentYearMonthPrefix(),

      highestSequence: 0,
    };
  }

  const prefixCounts =
    new Map();

  validReceipts.forEach(
    (receipt) => {
      prefixCounts.set(
        receipt.prefix,
        (prefixCounts.get(
          receipt.prefix
        ) || 0) + 1
      );
    }
  );

  const eventPrefix =
    Array.from(
      prefixCounts.entries()
    ).sort(
      (first, second) =>
        second[1] - first[1] ||
        second[0].localeCompare(
          first[0]
        )
    )[0][0];

  const highestSequence =
    validReceipts
      .filter(
        (receipt) =>
          receipt.prefix ===
          eventPrefix
      )
      .reduce(
        (highest, receipt) =>
          Math.max(
            highest,
            receipt.sequence
          ),
        0
      );

  return {
    prefix: eventPrefix,
    highestSequence,
  };
};

const createInitialForm = (receiptNo = "") => ({
  payerType: "resident",
  type: "Donation",
  title: "",
  notes: "",
  payerName: "",
  payerUid: "",
  residentSelection: "",
  blockName: "",
  floorNumber: "",
  flatLabel: "",
  amount: "",
  receiptNo,
  method: "",
  receivedByUid: "",
  receivedByName: "",
});

export default function FundDonation({
  communityId,
  event,
  community,
  residents = [],
  canEditFunds,
  committeeOptions = [],
  onSaved,
  onClose,
}) {
  const eventId = event?.id;

  const entryFee =
  event?.entryFee || {};

const adultFee =
  Number(
    entryFee.adult
  ) || 0;

const childFee =
  Number(
    entryFee.child
  ) || 0;

const familyFee =
  Number(
    entryFee.familyFee
  ) || 0;

const freeAge =
  entryFee.freeBelowAge == null
    ? null
    : Number(
        entryFee.freeBelowAge
      );

const isFamilyMode =
  event?.collectionMode ===
  "family";

  const [form, setForm] = useState(() => createInitialForm());
  const [saving, setSaving] = useState(false);
  const [
  uploadingExcel,
  setUploadingExcel,
] = useState(false);
  const [loadingReceiptNumber, setLoadingReceiptNumber] = useState(false);
  const [error, setError] = useState("");

  const detailLabel = useMemo(() => {
    const labels = {
      Donation: "Donation Purpose",
      Sponsor: "Sponsor Name / Purpose",
      Gift: "Gift Name / Purpose",
      Sale: "Sale Item / Purpose",
      Other: "Purpose / Description",
    };

    return labels[form.type] || "Fund Purpose";
  }, [form.type]);

  const getNextReceiptNumber = useCallback(async () => {
    if (!eventId) {
      return buildReceiptNumber(currentYearMonthPrefix(), 1);
    }

    const fundsSnapshot = await getDocs(
      collection(db, "events", eventId, "funds")
    );

    const validReceipts = fundsSnapshot.docs
      .map((fundDocument) =>
        parseReceiptNumber(fundDocument.data()?.receiptNo)
      )
      .filter(Boolean);

    if (validReceipts.length === 0) {
      return buildReceiptNumber(currentYearMonthPrefix(), 1);
    }

    const prefixCounts = new Map();

    validReceipts.forEach((receipt) => {
      prefixCounts.set(
        receipt.prefix,
        (prefixCounts.get(receipt.prefix) || 0) + 1
      );
    });

    const eventPrefix = [...prefixCounts.entries()].sort(
      (first, second) =>
        second[1] - first[1] || second[0].localeCompare(first[0])
    )[0][0];

    const highestSequence = validReceipts
      .filter((receipt) => receipt.prefix === eventPrefix)
      .reduce(
        (highest, receipt) => Math.max(highest, receipt.sequence),
        0
      );

    return buildReceiptNumber(eventPrefix, highestSequence + 1);
  }, [eventId]);

  const loadNextReceiptNumber = useCallback(async () => {
    if (!eventId) {
      setForm(createInitialForm());
      return;
    }

    try {
      setLoadingReceiptNumber(true);
      setError("");

      const nextReceiptNumber = await getNextReceiptNumber();

      setForm((previous) => ({
        ...previous,
        receiptNo: nextReceiptNumber,
      }));
    } catch (receiptError) {
      console.error("Load receipt number error:", receiptError);

      setForm((previous) => ({
        ...previous,
        receiptNo: buildReceiptNumber(currentYearMonthPrefix(), 1),
      }));

      setError(
        receiptError?.code === "permission-denied"
          ? "Firestore rules are blocking access to existing receipt numbers."
          : receiptError?.message ||
              "Could not load the next receipt number."
      );
    } finally {
      setLoadingReceiptNumber(false);
    }
  }, [eventId, getNextReceiptNumber]);

  useEffect(() => {
    loadNextReceiptNumber();
  }, [loadNextReceiptNumber]);

  const blockOptions = useMemo(() => {
    const values = new Set();
    const communityBlocks = Array.isArray(community?.blocks)
      ? community.blocks
      : [];

    communityBlocks.forEach((block, index) => {
      const blockName = String(
        block?.name || block?.blockName || `Block-${index + 1}`
      ).trim();

      if (blockName) {
        values.add(blockName);
      }
    });

    (Array.isArray(residents) ? residents : []).forEach((resident) => {
      const blockName = String(
        resident?.unit?.blockName || resident?.blockName || ""
      ).trim();

      if (blockName) {
        values.add(blockName);
      }
    });

    return Array.from(values)
      .filter(Boolean)
      .sort((first, second) =>
        first.localeCompare(second, undefined, { sensitivity: "base" })
      );
  }, [community, residents]);

  const floorOptions = useMemo(() => {
    if (!form.blockName) {
      return [];
    }

    const values = new Set();
    const safeResidents = Array.isArray(residents) ? residents : [];

    safeResidents.forEach((resident) => {
      const residentBlock = String(
        resident?.unit?.blockName || resident?.blockName || ""
      ).trim();

      if (residentBlock !== form.blockName) {
        return;
      }

      const suppliedFloor =
        resident?.unit?.floorNumber ?? resident?.floorNumber;
      const flatLabel =
        resident?.unit?.flatLabel || resident?.flatLabel;
      const floor =
        suppliedFloor !== "" && suppliedFloor != null
          ? Number(suppliedFloor)
          : extractFloor(flatLabel);

      if (Number.isFinite(floor)) {
        values.add(String(floor));
      }
    });

    const communityBlocks = Array.isArray(community?.blocks)
      ? community.blocks
      : [];

    const selectedBlock = communityBlocks.find((block, index) => {
      const blockName = String(
        block?.name || block?.blockName || `Block-${index + 1}`
      ).trim();

      return blockName === form.blockName;
    });

    if (selectedBlock?.hasGround) {
      values.add("0");
    }

    const numberOfFloors = Number(selectedBlock?.floors) || 0;

    for (
      let floorNumber = 1;
      floorNumber <= numberOfFloors;
      floorNumber += 1
    ) {
      values.add(String(floorNumber));
    }

    const configuredFlats = Array.isArray(selectedBlock?.flats)
      ? selectedBlock.flats
      : [];

    configuredFlats.forEach((flatLabel) => {
      const floor = extractFloor(flatLabel);

      if (floor != null) {
        values.add(String(floor));
      }
    });

    return Array.from(values)
      .sort((first, second) => Number(first) - Number(second))
      .map((value) => ({
        value,
        label: value === "0" ? "Ground Floor" : `Floor ${value}`,
      }));
  }, [community, form.blockName, residents]);

  const flatOptions = useMemo(() => {
    if (!form.blockName || form.floorNumber === "") {
      return [];
    }

    const values = new Set();
    const safeResidents = Array.isArray(residents) ? residents : [];

    safeResidents.forEach((resident) => {
      const residentBlock = String(
        resident?.unit?.blockName || resident?.blockName || ""
      ).trim();

      if (residentBlock !== form.blockName) {
        return;
      }

      const flatLabel = String(
        resident?.unit?.flatLabel || resident?.flatLabel || ""
      ).trim();
      const suppliedFloor =
        resident?.unit?.floorNumber ?? resident?.floorNumber;
      const residentFloor =
        suppliedFloor !== "" && suppliedFloor != null
          ? String(suppliedFloor)
          : String(extractFloor(flatLabel) ?? "");

      if (
        residentFloor === String(form.floorNumber) &&
        flatLabel
      ) {
        values.add(flatLabel);
      }
    });

    const communityBlocks = Array.isArray(community?.blocks)
      ? community.blocks
      : [];

    const selectedBlock = communityBlocks.find((block, index) => {
      const blockName = String(
        block?.name || block?.blockName || `Block-${index + 1}`
      ).trim();

      return blockName === form.blockName;
    });

    const configuredFlats = Array.isArray(selectedBlock?.flats)
      ? selectedBlock.flats
      : [];

    configuredFlats.forEach((flatLabel) => {
      if (
        String(extractFloor(flatLabel) ?? "") ===
        String(form.floorNumber)
      ) {
        values.add(String(flatLabel).trim());
      }
    });

    return Array.from(values)
      .filter(Boolean)
      .sort((first, second) =>
        first.localeCompare(second, undefined, { sensitivity: "base" })
      )
      .map((value) => ({ value, label: value }));
  }, [community, form.blockName, form.floorNumber, residents]);

  const flatResidents = useMemo(() => {
    if (!form.blockName || !form.flatLabel) {
      return [];
    }

    const uniqueResidents = new Map();

    (Array.isArray(residents) ? residents : [])
      .filter((resident) => {
        const residentBlock = String(
          resident?.unit?.blockName || resident?.blockName || ""
        ).trim();
        const residentFlat = String(
          resident?.unit?.flatLabel || resident?.flatLabel || ""
        ).trim();

        return (
          residentBlock === form.blockName &&
          residentFlat === form.flatLabel
        );
      })
      .forEach((resident, index) => {
        const uid = String(resident?.uid || resident?.id || "").trim();
        const name = String(
          resident?.fullName ||
            resident?.displayName ||
            resident?.name ||
            `Resident ${index + 1}`
        ).trim();
        const value = uid
          ? `resident:${uid}`
          : `resident-name:${name}`;

        if (!uniqueResidents.has(value)) {
          uniqueResidents.set(value, { value, uid, name });
        }
      });

    return Array.from(uniqueResidents.values()).sort((first, second) =>
      first.name.localeCompare(second.name, undefined, {
        sensitivity: "base",
      })
    );
  }, [form.blockName, form.flatLabel, residents]);

  const handlePayerTypeChange = (eventValue) => {
    const payerType = eventValue.target.value;

    setForm((previous) => ({
      ...createInitialForm(previous.receiptNo),
      payerType,
      type: previous.type || "Donation",
      title: previous.title || "",
    }));
  };

  const handleResidentSelectionChange = (eventValue) => {
    const selectedValue = eventValue.target.value;

    if (!selectedValue || selectedValue === "other") {
      setForm((previous) => ({
        ...previous,
        residentSelection: selectedValue,
        payerUid: "",
        payerName: "",
      }));
      return;
    }

    const selectedResident = flatResidents.find(
      (resident) => resident.value === selectedValue
    );

    setForm((previous) => ({
      ...previous,
      residentSelection: selectedResident?.value || "",
      payerUid: selectedResident?.uid || "",
      payerName: selectedResident?.name || "",
    }));
  };

  const handleCommitteeChange = (eventValue) => {
    const selectedOption = committeeOptions.find(
      (option) => option.value === eventValue.target.value
    );

    setForm((previous) => ({
      ...previous,
      receivedByUid: selectedOption?.uid || "",
      receivedByName: selectedOption?.label || "",
    }));
  };

  const resetFormWithNewReceipt = async () => {
    try {
      setLoadingReceiptNumber(true);
      setError("");

      const nextReceiptNumber = await getNextReceiptNumber();

      setForm(createInitialForm(nextReceiptNumber));
    } catch (receiptError) {
      console.error("Reset receipt number error:", receiptError);
      setError(
        receiptError?.message || "Could not load the next receipt number."
      );
    } finally {
      setLoadingReceiptNumber(false);
    }
  };


//  const downloadCombinedTemplate = () => {
//   const contributionRows = [
//     {
//       blockName: "A",
//       floorNumber: 1,
//       flatLabel: "A-101",
//       paidByName: "Resident Name",
//       adults: 2,
//       kids: 1,
//       freeKids: 0,
//       amountMode: isFamilyMode ? "manual" : "auto",
//       amount: isFamilyMode ? familyFee : "",
//       method: "Cash",
//       receivedByUid: "",
//       receivedByName: "Committee Member",
//     },
//   ];

//   const donationRows = [
//     {
//       payerType: "other",
//       fundType: "Donation",
//       purpose: "Festival Donation",
//       payerName: "Donor Name",
//       blockName: "",
//       floorNumber: "",
//       flatLabel: "",
//       amount: 1000,
//       method: "Cash",
//       receivedByUid: "",
//       receivedByName: "Committee Member",
//       notes: "",
//     },
//     {
//       payerType: "resident",
//       fundType: "Sponsor",
//       purpose: "Stage Sponsor",
//       payerName: "Resident Name",
//       blockName: "A",
//       floorNumber: 1,
//       flatLabel: "A-101",
//       amount: 5000,
//       method: "UPI",
//       receivedByUid: "",
//       receivedByName: "Committee Member",
//       notes: "",
//     },
//   ];

//   const workbook = XLSX.utils.book_new();

//   const contributionSheet =
//     XLSX.utils.json_to_sheet(contributionRows);

//   const donationSheet =
//     XLSX.utils.json_to_sheet(donationRows);

//   contributionSheet["!cols"] = [
//     { wch: 15 },
//     { wch: 15 },
//     { wch: 15 },
//     { wch: 25 },
//     { wch: 10 },
//     { wch: 10 },
//     { wch: 12 },
//     { wch: 15 },
//     { wch: 15 },
//     { wch: 18 },
//     { wch: 20 },
//     { wch: 25 },
//   ];

//   donationSheet["!cols"] = [
//     { wch: 15 },
//     { wch: 15 },
//     { wch: 30 },
//     { wch: 25 },
//     { wch: 15 },
//     { wch: 15 },
//     { wch: 15 },
//     { wch: 15 },
//     { wch: 15 },
//     { wch: 20 },
//     { wch: 25 },
//     { wch: 30 },
//   ];

//   XLSX.utils.book_append_sheet(
//     workbook,
//     contributionSheet,
//     "Contributions"
//   );

//   XLSX.utils.book_append_sheet(
//     workbook,
//     donationSheet,
//     "Donations"
//   );

//   const eventName = sanitizeFileName(
//     event?.name ||
//       event?.title ||
//       "Event"
//   );

//   XLSX.writeFile(
//     workbook,
//     `Fund_Template_${eventName}.xlsx`
//   );
// }
  
const downloadCombinedTemplate = () => {
  const contributionRows = [
    {
      blockName: "A",
      flatLabel: "A-101",
      paidByName: "Resident Name",
      adults: 2,
      kids: 1,
      freeKids: 0,
      amountMode: isFamilyMode ? "manual" : "auto",
      amount: isFamilyMode ? familyFee : "",
      method: "Cash",
      receivedByUid: "",
      receivedByName: "Committee Member",
    },
  ];

  const donationRows = [
    {
      payerType: "other",
      fundType: "Donation",
      purpose: "Festival Donation",
      payerName: "Donor Name",
      blockName: "",
      floorNumber: "",
      flatLabel: "",
      amount: 1000,
      method: "Cash",
      receivedByUid: "",
      receivedByName: "Committee Member",
      notes: "",
    },
    {
      payerType: "resident",
      fundType: "Sponsor",
      purpose: "Stage Sponsor",
      payerName: "Resident Name",
      blockName: "A",
      floorNumber: 1,
      flatLabel: "A-101",
      amount: 5000,
      method: "UPI",
      receivedByUid: "",
      receivedByName: "Committee Member",
      notes: "",
    },
  ];

  const workbook = XLSX.utils.book_new();

  const contributionSheet =
    XLSX.utils.json_to_sheet(contributionRows);

  const donationSheet =
    XLSX.utils.json_to_sheet(donationRows);

  contributionSheet["!cols"] = [
    { wch: 15 },
    { wch: 15 },
    { wch: 25 },
    { wch: 10 },
    { wch: 10 },
    { wch: 12 },
    { wch: 15 },
    { wch: 15 },
    { wch: 18 },
    { wch: 20 },
    { wch: 25 },
  ];

  donationSheet["!cols"] = [
    { wch: 15 },
    { wch: 15 },
    { wch: 30 },
    { wch: 25 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
    { wch: 20 },
    { wch: 25 },
    { wch: 30 },
  ];

  XLSX.utils.book_append_sheet(
    workbook,
    contributionSheet,
    "Contributions"
  );

  XLSX.utils.book_append_sheet(
    workbook,
    donationSheet,
    "Donations"
  );

  const eventName = sanitizeFileName(
    event?.name ||
      event?.title ||
      "Event"
  );

  XLSX.writeFile(
    workbook,
    `Fund_Template_${eventName}.xlsx`
  );
};


  const uploadCombinedTemplate =
  async (file) => {
    if (!canEditFunds) {
      alert(
        "You are not allowed to upload funds."
      );
      return;
    }

    if (!eventId) {
      alert(
        "Event ID is missing."
      );
      return;
    }

    if (!communityId) {
      alert(
        "Community ID is missing."
      );
      return;
    }

    if (!file) {
      return;
    }

    try {
      setUploadingExcel(true);
      setError("");

      const workbook =
        XLSX.read(
          await file.arrayBuffer(),
          {
            type: "array",
          }
        );

      const contributionSheet =
        workbook.Sheets[
          "Contributions"
        ];

      const donationSheet =
        workbook.Sheets[
          "Donations"
        ];

      const contributionRows =
        contributionSheet
          ? XLSX.utils.sheet_to_json(
              contributionSheet,
              {
                defval: "",
              }
            )
          : [];

      const donationRows =
        donationSheet
          ? XLSX.utils.sheet_to_json(
              donationSheet,
              {
                defval: "",
              }
            )
          : [];

    //   const contributionPayloads =
    //     contributionRows
    //       .map((row) => {
    //         const blockName =
    //           String(
    //             row.blockName ||
    //               ""
    //           ).trim();

    //         const flatLabel =
    //           String(
    //             row.flatLabel ||
    //               ""
    //           ).trim();

    //         const paidByName =
    //           String(
    //             row.paidByName ||
    //               ""
    //           ).trim();

    //         const method =
    //           String(
    //             row.method || ""
    //           ).trim();

    //         const receivedByName =
    //           String(
    //             row.receivedByName ||
    //               ""
    //           ).trim();

    //         const amountMode =
    //           String(
    //             row.amountMode ||
    //               (isFamilyMode
    //                 ? "manual"
    //                 : "auto")
    //           )
    //             .trim()
    //             .toLowerCase() ===
    //           "manual"
    //             ? "manual"
    //             : "auto";

    //         const adults =
    //           Math.max(
    //             0,
    //             Number(
    //               row.adults
    //             ) || 0
    //           );

    //         const kids =
    //           Math.max(
    //             0,
    //             Number(
    //               row.kids
    //             ) || 0
    //           );

    //         const freeKids =
    //           Math.min(
    //             kids,
    //             Math.max(
    //               0,
    //               Number(
    //                 row.freeKids
    //               ) || 0
    //             )
    //           );

    //         const amount =
    //           isFamilyMode ||
    //           amountMode ===
    //             "manual"
    //             ? Number(
    //                 row.amount ||
    //                   familyFee
    //               )
    //             : calculateAutoAmount(
    //                 {
    //                   adultFee,
    //                   childFee,
    //                   adults,
    //                   kids,
    //                   freeKids,
    //                 }
    //               );

    //         if (
    //           !blockName ||
    //           !flatLabel ||
    //           !paidByName ||
    //           !method ||
    //           !receivedByName ||
    //           !Number.isFinite(
    //             amount
    //           ) ||
    //           amount <= 0
    //         ) {
    //           return null;
    //         }

    //         const suppliedFloor =
    //           row.floorNumber;

    //         const floorNumber =
    //           suppliedFloor !== "" &&
    //           suppliedFloor != null
    //             ? Number(
    //                 suppliedFloor
    //               )
    //             : extractFloor(
    //                 flatLabel
    //               );

    //         return cleanObject({
    //           sourceType:
    //             "resident",

    //           payerType:
    //             "resident",

    //           communityId:
    //             String(
    //               communityId ||
    //                 ""
    //             ).trim(),

    //           eventId,

    //           blockName,

    //           floorNumber:
    //             Number.isFinite(
    //               floorNumber
    //             )
    //               ? floorNumber
    //               : undefined,

    //           flatLabel,

    //           paidByName,

    //           amount,

    //           method,

    //           receivedByUid:
    //             String(
    //               row.receivedByUid ||
    //                 ""
    //             ).trim() ||
    //             undefined,

    //           receivedByName,

    //           amountMode,

    //           householdCounts: {
    //             adults,
    //             kids,
    //             freeKids,

    //             chargeableKids:
    //               Math.max(
    //                 0,
    //                 kids - freeKids
    //               ),

    //             totalMembers:
    //               adults + kids,
    //           },

    //           entryFeeUsed: {
    //             adult:
    //               adultFee,

    //             child:
    //               childFee,

    //             familyFee,

    //             freeBelowAge:
    //               freeAge,
    //           },

    //           uploadedFrom:
    //             "combined-excel",
    //         });
    //       })
    //       .filter(Boolean);

    const contributionPayloads =
  contributionRows
    .map((row) => {
      const blockName = String(
        row.blockName || ""
      ).trim();

      const flatLabel = String(
        row.flatLabel || ""
      ).trim();

      const paidByName = String(
        row.paidByName || ""
      ).trim();

      const method = String(
        row.method || ""
      ).trim();

      const receivedByUid = String(
        row.receivedByUid || ""
      ).trim();

      const receivedByName = String(
        row.receivedByName || ""
      ).trim();

      const amountMode =
        String(
          row.amountMode ||
            (isFamilyMode
              ? "manual"
              : "auto")
        )
          .trim()
          .toLowerCase() === "manual"
          ? "manual"
          : "auto";

      const adults = Math.max(
        0,
        Number(row.adults) || 0
      );

      const kids = Math.max(
        0,
        Number(row.kids) || 0
      );

      const freeKids = Math.min(
        kids,
        Math.max(
          0,
          Number(row.freeKids) || 0
        )
      );

      const manualAmount =
        row.amount !== "" &&
        row.amount != null
          ? Number(row.amount)
          : familyFee;

      const amount =
        isFamilyMode ||
        amountMode === "manual"
          ? manualAmount
          : calculateAutoAmount({
              adultFee,
              childFee,
              adults,
              kids,
              freeKids,
            });

      const floorNumber =
        extractFloor(flatLabel);

      if (
        !blockName ||
        !flatLabel ||
        !paidByName ||
        !method ||
        (!receivedByUid &&
          !receivedByName) ||
        !Number.isFinite(amount) ||
        amount <= 0
      ) {
        return null;
      }

      return cleanObject({
        sourceType: "resident",
        payerType: "resident",

        communityId: String(
          communityId || ""
        ).trim(),

        eventId,

        blockName,

        floorNumber:
          floorNumber == null
            ? undefined
            : floorNumber,

        flatLabel,

        paidByName,

        amount,

        method,

        receivedByUid:
          receivedByUid || undefined,

        receivedByName:
          receivedByName || undefined,

        amountMode,

        householdCounts: {
          adults,
          kids,
          freeKids,
          chargeableKids: Math.max(
            0,
            kids - freeKids
          ),
          totalMembers:
            adults + kids,
        },

        entryFeeUsed: {
          adult: adultFee,
          child: childFee,
          familyFee,
          freeBelowAge: freeAge,
        },

        uploadedFrom:
          "combined-excel",
      });
    })
    .filter(Boolean);

      const donationPayloads =
        donationRows
          .map((row) => {
            const payerType =
              String(
                row.payerType ||
                  "other"
              )
                .trim()
                .toLowerCase() ===
              "resident"
                ? "resident"
                : "other";

            const otherType =
              normalizeFundType(
                row.fundType ||
                  row.otherType
              );

            const otherTitle =
              String(
                row.purpose ||
                  row.otherTitle ||
                  ""
              ).trim();

            const paidByName =
              String(
                row.payerName ||
                  row.paidByName ||
                  ""
              ).trim();

            const amount =
              Number(row.amount);

            const method =
              String(
                row.method || ""
              ).trim();

            const receivedByName =
              String(
                row.receivedByName ||
                  ""
              ).trim();

              const receivedByUid =
  String(
    row.receivedByUid || "")

            if (
              !otherTitle ||
              !paidByName ||
              !method ||
              !receivedByName ||
              !Number.isFinite(
                amount
              ) ||
              amount <= 0
            ) {
              return null;
            }

            const blockName =
              payerType ===
              "resident"
                ? String(
                    row.blockName ||
                      ""
                  ).trim()
                : "";

            const flatLabel =
              payerType ===
              "resident"
                ? String(
                    row.flatLabel ||
                      ""
                  ).trim()
                : "";

            if (
              payerType ===
                "resident" &&
              (!blockName ||
                !flatLabel)
            ) {
              return null;
            }

            const suppliedFloor =
              row.floorNumber;

            const floorNumber =
              payerType ===
              "resident"
                ? suppliedFloor !==
                    "" &&
                  suppliedFloor !=
                    null
                  ? Number(
                      suppliedFloor
                    )
                  : extractFloor(
                      flatLabel
                    )
                : undefined;

            return cleanObject({
              sourceType:
                "other",

              payerType,

              communityId:
                String(
                  communityId ||
                    ""
                ).trim(),

              eventId,

              otherType,

              otherTitle,

              otherNotes:
                String(
                  row.notes ||
                    row.otherNotes ||
                    ""
                ).trim() ||
                undefined,

              blockName:
                payerType ===
                "resident"
                  ? blockName
                  : undefined,

              floorNumber:
                payerType ===
                  "resident" &&
                Number.isFinite(
                  floorNumber
                )
                  ? floorNumber
                  : undefined,

              flatLabel:
                payerType ===
                "resident"
                  ? flatLabel
                  : undefined,

              paidByName,

              amount,

              method,

              receivedByUid:
                String(
                  row.receivedByUid ||
                    ""
                ).trim() ||
                undefined,

              receivedByName,

              amountMode:
                "manual",

              uploadedFrom:
                "combined-excel",
            });
          })
          .filter(Boolean);

      const allPayloads = [
        ...contributionPayloads,
        ...donationPayloads,
      ];

      if (
        allPayloads.length === 0
      ) {
        alert(
          "No valid Contribution or Donation rows were found."
        );
        return;
      }

      const receiptSeries =
        await getReceiptSeries(
          eventId
        );

      for (
        let start = 0;
        start <
        allPayloads.length;
        start += 450
      ) {
        const batch =
          writeBatch(db);

        const currentPayloads =
          allPayloads.slice(
            start,
            start + 450
          );

        currentPayloads.forEach(
          (payload, index) => {
            const sequence =
              receiptSeries
                .highestSequence +
              start +
              index +
              1;

            const receiptNo =
              buildReceiptNumber(
                receiptSeries.prefix,
                sequence
              );

            const fundReference =
              doc(
                collection(
                  db,
                  "events",
                  eventId,
                  "funds"
                )
              );

            batch.set(
              fundReference,
              {
                ...payload,

                receiptNo,

                receiptPrefix:
                  receiptSeries.prefix,

                receiptSequence:
                  sequence,

                createdAt:
                  serverTimestamp(),
              }
            );
          }
        );

        await batch.commit();
      }

      const skippedContributions =
        contributionRows.length -
        contributionPayloads.length;

      const skippedDonations =
        donationRows.length -
        donationPayloads.length;

      alert(
        [
          "Fund Excel upload completed.",
          `Contributions uploaded: ${contributionPayloads.length}`,
          `Donations uploaded: ${donationPayloads.length}`,
          `Contribution rows skipped: ${skippedContributions}`,
          `Donation rows skipped: ${skippedDonations}`,
        ].join("\n")
      );

      await loadNextReceiptNumber();

      onSaved?.();
    } catch (uploadError) {
      console.error(
        "Combined fund upload error:",
        uploadError
      );

      const message =
        uploadError?.message ||
        "Failed to upload the fund Excel file.";

      setError(message);
      alert(message);
    } finally {
      setUploadingExcel(false);
    }
  };

  const save = async () => {
    setError("");

    if (!canEditFunds) {
      alert("You are not allowed to add funds.");
      return;
    }

    if (!eventId) {
      alert("Event ID is missing.");
      return;
    }

    if (!communityId) {
      alert("Community ID is missing.");
      return;
    }

    const fundPurpose = String(form.title || "").trim();

    if (!fundPurpose) {
      alert(`Enter ${detailLabel}.`);
      return;
    }

    const isResidentPayer = form.payerType === "resident";
    const isOtherResident = form.residentSelection === "other";

    if (isResidentPayer) {
      if (
        !form.blockName ||
        form.floorNumber === "" ||
        !form.flatLabel
      ) {
        alert("Select block, floor and flat.");
        return;
      }

      if (!form.residentSelection) {
        alert("Select a registered resident or Other Name.");
        return;
      }
    }

    const paidByName = String(form.payerName || "").trim();

    if (!paidByName) {
      alert(
        isResidentPayer && isOtherResident
          ? "Enter the resident name."
          : isResidentPayer
            ? "Select a registered resident."
            : "Enter payer, donor or sponsor name."
      );
      return;
    }

    const amount = Number(form.amount);

    if (!Number.isFinite(amount) || amount <= 0) {
      alert("Amount must be greater than 0.");
      return;
    }

    if (!form.method) {
      alert("Select pay mode.");
      return;
    }

    if (!form.receivedByUid && !form.receivedByName) {
      alert("Select received by.");
      return;
    }

    try {
      setSaving(true);

      // Refresh immediately before saving so Contribution and Donation
      // use the same event-level funds sequence.
      const receiptNo = await getNextReceiptNumber();

      const donationPayload = cleanObject({
        sourceType: "other",
        payerType: form.payerType,
        communityId: String(communityId || "").trim(),
        eventId,
        otherType: form.type,
        otherTitle: fundPurpose,
        otherNotes: String(form.notes || "").trim() || undefined,
        blockName: isResidentPayer
          ? String(form.blockName || "").trim()
          : undefined,
        floorNumber: isResidentPayer
          ? Number(form.floorNumber)
          : undefined,
        flatLabel: isResidentPayer
          ? String(form.flatLabel || "").trim()
          : undefined,
        residentSelection: isResidentPayer
          ? form.residentSelection
          : undefined,
        payerUid:
          isResidentPayer && !isOtherResident
            ? String(form.payerUid || "").trim() || undefined
            : undefined,
        paidByName,
        amount,
        receiptNo,
        receiptSequence: parseReceiptNumber(receiptNo)?.sequence || 1,
        receiptPrefix: parseReceiptNumber(receiptNo)?.prefix || currentYearMonthPrefix(),
        method: form.method,
        receivedByUid:
          String(form.receivedByUid || "").trim() || undefined,
        receivedByName: String(form.receivedByName || "").trim(),
        amountMode: "manual",
        createdAt: serverTimestamp(),
      });

      const savedDocument = await addDoc(
        collection(db, "events", eventId, "funds"),
        donationPayload
      );

      const savedFund = {
        id: savedDocument.id,
        ...donationPayload,
      };

      alert(
        `${form.type} fund added successfully. Receipt Number: ${receiptNo}`
      );

      onSaved?.(savedFund);
      setForm(createInitialForm());
      onClose?.();
    } catch (saveError) {
      console.error("Save FundDonation error:", saveError);

      const message =
        saveError?.code === "permission-denied"
          ? "Missing or insufficient permissions. Firestore rules are blocking this fund read or write."
          : saveError?.message ||
            "Failed to add donation or other fund.";

      setError(message);
      alert(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Row className="g-3">
        <Col md={3}>
          <Form.Label>Payer Type</Form.Label>
          <Form.Select
            value={form.payerType}
            onChange={handlePayerTypeChange}
            disabled={saving}
          >
            <option value="resident">Resident</option>
            <option value="other">Other / External</option>
          </Form.Select>
        </Col>

        <Col md={3}>
          <Form.Label>Fund Type</Form.Label>
          <Form.Select
            value={form.type}
            disabled={saving}
            onChange={(eventValue) =>
              setForm((previous) => ({
                ...previous,
                type: eventValue.target.value,
                title: "",
              }))
            }
          >
            {FUND_TYPES.map((fundType) => (
              <option key={fundType} value={fundType}>
                {fundType}
              </option>
            ))}
          </Form.Select>
        </Col>

        <Col md={6}>
          <Form.Label>{detailLabel}</Form.Label>
          <Form.Control
            value={form.title}
            disabled={saving}
            placeholder={`Enter ${detailLabel.toLowerCase()}`}
            onChange={(eventValue) =>
              setForm((previous) => ({
                ...previous,
                title: eventValue.target.value,
              }))
            }
          />
        </Col>

        {form.payerType === "resident" ? (
          <>
            <Col md={3}>
              <Form.Label>Block</Form.Label>
              <Form.Select
                value={form.blockName}
                disabled={saving}
                onChange={(eventValue) =>
                  setForm((previous) => ({
                    ...previous,
                    blockName: eventValue.target.value,
                    floorNumber: "",
                    flatLabel: "",
                    residentSelection: "",
                    payerUid: "",
                    payerName: "",
                  }))
                }
              >
                <option value="">Select Block</option>
                {blockOptions.map((blockName) => (
                  <option key={blockName} value={blockName}>
                    {blockName}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col md={3}>
              <Form.Label>Floor</Form.Label>
              <Form.Select
                value={form.floorNumber}
                disabled={saving || !form.blockName}
                onChange={(eventValue) =>
                  setForm((previous) => ({
                    ...previous,
                    floorNumber: eventValue.target.value,
                    flatLabel: "",
                    residentSelection: "",
                    payerUid: "",
                    payerName: "",
                  }))
                }
              >
                <option value="">Select Floor</option>
                {floorOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col md={3}>
              <Form.Label>Flat</Form.Label>
              <Form.Select
                value={form.flatLabel}
                disabled={saving || form.floorNumber === ""}
                onChange={(eventValue) =>
                  setForm((previous) => ({
                    ...previous,
                    flatLabel: eventValue.target.value,
                    residentSelection: "",
                    payerUid: "",
                    payerName: "",
                  }))
                }
              >
                <option value="">Select Flat</option>
                {flatOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col md={3}>
              <Form.Label>Resident Name</Form.Label>
              <Form.Select
                value={form.residentSelection}
                disabled={saving || !form.flatLabel}
                onChange={handleResidentSelectionChange}
              >
                <option value="">
                  {form.flatLabel ? "Select Resident" : "Select Flat First"}
                </option>
                {flatResidents.map((resident) => (
                  <option key={resident.value} value={resident.value}>
                    {resident.name}
                  </option>
                ))}
                <option value="other">Other Name</option>
              </Form.Select>
            </Col>

            {form.residentSelection === "other" && (
              <Col md={3}>
                <Form.Label>Enter Resident Name</Form.Label>
                <Form.Control
                  value={form.payerName}
                  disabled={saving}
                  onChange={(eventValue) =>
                    setForm((previous) => ({
                      ...previous,
                      payerUid: "",
                      payerName: eventValue.target.value,
                    }))
                  }
                />
              </Col>
            )}
          </>
        ) : (
          <Col md={3}>
            <Form.Label>Payer / Donor / Sponsor Name</Form.Label>
            <Form.Control
              value={form.payerName}
              disabled={saving}
              onChange={(eventValue) =>
                setForm((previous) => ({
                  ...previous,
                  payerName: eventValue.target.value,
                  payerUid: "",
                }))
              }
            />
          </Col>
        )}

        <Col md={3}>
          <Form.Label>Amount</Form.Label>
          <Form.Control
            type="number"
            min="0"
            step="0.01"
            value={form.amount}
            disabled={saving}
            onChange={(eventValue) =>
              setForm((previous) => ({
                ...previous,
                amount: eventValue.target.value,
              }))
            }
          />
        </Col>

        <Col md={3}>
          <Form.Label>Receipt No</Form.Label>
          <div className="position-relative">
            <Form.Control
              value={form.receiptNo}
              readOnly
              className="bg-light"
              placeholder={
                loadingReceiptNumber
                  ? "Loading receipt number..."
                  : "Receipt number unavailable"
              }
            />
            {loadingReceiptNumber && (
              <Spinner
                animation="border"
                size="sm"
                className="position-absolute top-50 end-0 translate-middle-y me-2"
              />
            )}
          </div>
          <Form.Text className="text-muted">
            Final number is checked again when saving.
          </Form.Text>
        </Col>

        <Col md={3}>
          <Form.Label>Pay Mode</Form.Label>
          <Form.Select
            value={form.method}
            disabled={saving}
            onChange={(eventValue) =>
              setForm((previous) => ({
                ...previous,
                method: eventValue.target.value,
              }))
            }
          >
            <option value="">Select Pay Mode</option>
            {PAYMENT_METHODS.map((method) => (
              <option key={method} value={method}>
                {method}
              </option>
            ))}
          </Form.Select>
        </Col>

        <Col md={3}>
          <Form.Label>Received By</Form.Label>
          <Form.Select
            value={
              form.receivedByUid
                ? `uid:${form.receivedByUid}`
                : form.receivedByName
                  ? `name:${form.receivedByName}`
                  : ""
            }
            disabled={saving}
            onChange={handleCommitteeChange}
          >
            <option value="">Select Committee Member</option>
            {committeeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        </Col>

        <Col md={12}>
          <Form.Label>Additional Notes</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={form.notes}
            disabled={saving}
            onChange={(eventValue) =>
              setForm((previous) => ({
                ...previous,
                notes: eventValue.target.value,
              }))
            }
          />
        </Col>
      </Row>

      {/* <div className="d-flex justify-content-end gap-2 mt-4">
        <Button
          type="button"
          variant="outline-secondary"
          disabled={saving || loadingReceiptNumber}
          onClick={resetFormWithNewReceipt}
        >
          Reset
        </Button>

        <Button
          type="button"
          variant="primary"
          onClick={save}
          disabled={saving || loadingReceiptNumber}
        >
          {saving ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Saving...
            </>
          ) : (
            `Add ${form.type || "Other"} Fund`
          )}
        </Button>
      </div> */}

      <div className="d-flex flex-wrap justify-content-end gap-2 mt-4">
  <Button
    type="button"
    variant="outline-secondary"
    disabled={
      saving ||
      uploadingExcel ||
      loadingReceiptNumber
    }
    onClick={
      resetFormWithNewReceipt
    }
  >
    Reset
  </Button>

  <Button
    type="button"
    variant="outline-success"
    disabled={
      saving ||
      uploadingExcel
    }
    onClick={
      downloadCombinedTemplate
    }
  >
    Download Fund Template
  </Button>

  <Form.Label
    className={`btn btn-outline-primary mb-0 ${
      uploadingExcel ||
      saving
        ? "disabled"
        : ""
    }`}
  >
    {uploadingExcel
      ? "Uploading..."
      : "Upload Fund Excel"}

    <Form.Control
      hidden
      type="file"
      accept=".xlsx,.xls"
      disabled={
        uploadingExcel ||
        saving
      }
      onChange={async (
        eventValue
      ) => {
        const file =
          eventValue.target
            .files?.[0];

        await uploadCombinedTemplate(
          file
        );

        eventValue.target.value =
          "";
      }}
    />
  </Form.Label>

  <Button
    type="button"
    variant="primary"
    onClick={save}
    disabled={
      saving ||
      uploadingExcel ||
      loadingReceiptNumber ||
      !form.receiptNo
    }
  >
    {saving ? (
      <>
        <Spinner
          animation="border"
          size="sm"
          className="me-2"
        />
        Saving...
      </>
    ) : (
      `Add ${
        form.type || "Other"
      } Fund`
    )}
  </Button>
</div>
    </div>
  );
}
